// apps/provider/src/services/api/exercises.js
// Exercise search, favorites, prescriptions, media upload, AI routine generation,
// and batch exercise retrieval.
//
// Methods: searchExercises, getExerciseFilters, getExercise, createExercise, updateExercise,
//          getFavoriteExercises, addFavoriteExercise, removeFavoriteExercise,
//          createExercisePrescription, updateExercisePrescription, getExercisePrescriptions,
//          duplicateExercisePrescription, getExercisePrescription, getExercisePrescriptionPublic,
//          deleteExercisePrescription,
//          generateRoutineStream, getExercisesByIds,
//          uploadExerciseMedia, uploadExerciseFromUrl
//
// Response types: @prism-ehr/shared → Exercise, ExerciseSearchResult, ExerciseFilters,
//                 UserFavoriteExercise, ExercisePrescription, ExerciseMediaUploadResult,
//                 ExerciseCompletion, RoutineStreamChunk

import { authService } from '../auth';

/**
 * @returns {Object} Exercise domain methods (bound to `this` of the composed apiService)
 */
export function createExercisesMethods() {
  return {

    // ============ Exercise Search & Lookup ============

    /**
     * Search exercises with filters (body part, type, equipment, difficulty, etc.).
     *
     * Xano: GET /exercises/search
     * Xano staging file: 675_exercises_search_GET.xs
     * Xano production file: 424_exercises_search_GET.xs
     *
     * @param {Object} params
     * @param {string} [params.q] - Search query
     * @param {string|string[]} [params.body_part] - Body part filter(s)
     * @param {string|string[]} [params.exercise_type] - Exercise type filter(s)
     * @param {string|string[]} [params.equipment] - Equipment filter(s)
     * @param {string|string[]} [params.cpg_tags] - CPG tag filter(s)
     * @param {string|string[]} [params.motion] - Motion filter(s)
     * @param {string|string[]} [params.exercise_ids] - Specific exercise IDs
     * @param {number} [params.difficulty_min] - Minimum difficulty
     * @param {number} [params.difficulty_max] - Maximum difficulty
     * @param {boolean} [params.favorites_only] - Only return favorites
     * @param {string} [params.organization_id] - Scope to exercises owned by this organization
     * @param {string} [params.suitable_for] - Suitability filter
     * @param {number} [params.page] - Page number
     * @param {number} [params.limit] - Results per page
     * @returns {Promise<import('@prism-ehr/shared').ExerciseSearchResult>}
     */
    async searchExercises(params) {
      const queryParams = new URLSearchParams();

      if (params.q) queryParams.append('q', params.q);
      if (params.body_part) {
        if (Array.isArray(params.body_part)) {
          params.body_part.forEach(bp => queryParams.append('body_part[]', bp));
        } else {
          queryParams.append('body_part', params.body_part);
        }
      }
      if (params.exercise_type) {
        if (Array.isArray(params.exercise_type)) {
          params.exercise_type.forEach(ty => queryParams.append('exercise_type[]', ty));
        } else {
          queryParams.append('exercise_type', params.exercise_type);
        }
      }
      if (params.equipment) {
        if (Array.isArray(params.equipment)) {
          params.equipment.forEach(eq => queryParams.append('equipment[]', eq));
        } else {
          queryParams.append('equipment', params.equipment);
        }
      }
      if (params.cpg_tags) {
        if (Array.isArray(params.cpg_tags)) {
          params.cpg_tags.forEach(cp => queryParams.append('cpg_tags[]', cp));
        } else {
          queryParams.append('cpg_tags', params.cpg_tags);
        }
      }
      if (params.motion) {
        if (Array.isArray(params.motion)) {
          params.motion.forEach(mt => queryParams.append('motion[]', mt));
        } else {
          queryParams.append('motion', params.motion);
        }
      }
      if (params.exercise_ids) {
        if (Array.isArray(params.exercise_ids)) {
          params.exercise_ids.forEach(ei => queryParams.append('exercise_ids[]', ei));
        } else {
          queryParams.append('exercise_ids', params.exercise_ids);
        }
      }
      if (params.difficulty_min) queryParams.append('difficulty_min', params.difficulty_min);
      if (params.difficulty_max) queryParams.append('difficulty_max', params.difficulty_max);
      if (params.favorites_only !== undefined) queryParams.append('favorites_only', params.favorites_only);
      if (params.organization_id) queryParams.append('organization_id', params.organization_id);
      if (params.suitable_for) queryParams.append('suitable_for', params.suitable_for);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      return this.request(`/exercises/search?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get available exercise filter options (body parts, types, equipment, etc.).
     *
     * Xano: GET /exercises/filters
     * Xano staging file: 676_exercises_filters_GET.xs
     * Xano production file: 421_exercises_filters_GET.xs
     *
     * @returns {Promise<import('@prism-ehr/shared').ExerciseFilters>}
     */
    async getExerciseFilters() {
      return this.request('/exercises/filters', {
        method: 'GET'
      });
    },

    /**
     * Get a single exercise by ID.
     *
     * Xano: GET /single-exercise/{exercise_id}
     * Created manually in Xano admin (both staging + production)
     *
     * @param {number|string} id - Exercise ID
     * @returns {Promise<import('@prism-ehr/shared').Exercise>}
     */
    async getExercise(id) {
      return this.request(`/single-exercise/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new custom (write-in) exercise.
     *
     * Xano: POST /exercise
     *
     * @param {Object} data
     * @param {string} data.official_title - Exercise name (required)
     * @param {string[]} [data.body_part] - Body part(s)
     * @param {string} [data.exercise_type] - Exercise type
     * @param {string[]} [data.equipment] - Equipment
     * @param {string} data.source - Should be 'custom'
     * @param {string} data.created_by - User ID of creator
     * @param {string} data.organization_id - Organization ID
     * @returns {Promise<import('@prism-ehr/shared').Exercise>}
     */
    async createExercise(data) {
      return this.request('/exercise', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update an existing exercise record.
     *
     * Xano: PATCH /exercise/{exercise_id}
     * Xano staging file: 662_exercise_exercise_id_PATCH.xs
     *
     * @param {string} exerciseId - Exercise UUID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').Exercise>}
     */
    async updateExercise(exerciseId, data) {
      return this.request(`/exercise/${exerciseId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    // ============ Exercise Favorites ============

    /**
     * Get current user's favorite exercises.
     *
     * Xano: GET /user_favorite_exercises
     * Xano staging file: 679_user_favorite_exercises_GET.xs
     * Xano production file: 333_user_favorite_exercises_GET.xs
     *
     * @returns {Promise<import('@prism-ehr/shared').UserFavoriteExercise[]>}
     */
    async getFavoriteExercises() {
      return this.request('/user_favorite_exercises', {
        method: 'GET'
      });
    },

    /**
     * Add an exercise to the current user's favorites.
     *
     * Xano: POST /user_favorite_exercises
     * Xano staging file: 680_user_favorite_exercises_POST.xs
     * Xano production file: 498_user_favorite_exercises_POST.xs
     *
     * @param {number|string} exerciseId - Exercise ID to favorite
     * @returns {Promise<import('@prism-ehr/shared').UserFavoriteExercise>}
     */
    async addFavoriteExercise(exerciseId) {
      return this.request('/user_favorite_exercises', {
        method: 'POST',
        body: JSON.stringify({ exercise_id: exerciseId })
      });
    },

    /**
     * Remove an exercise from the current user's favorites.
     *
     * Xano: DELETE /user_favorite_exercises/{id}
     * Xano staging file: 677_user_favorite_exercises_user_favorite_exercises_id_DELETE.xs
     * Xano production file: 499_user_favorite_exercises_user_favorite_exercises_id_DELETE.xs
     *
     * @param {number|string} exerciseId - Favorite exercise record ID
     * @returns {Promise<void>}
     */
    async removeFavoriteExercise(exerciseId) {
      return this.request(`/user_favorite_exercises/${parseInt(exerciseId)}`, {
        method: 'DELETE'
      });
    },

    // ============ Exercise Prescriptions ============

    /**
     * Create a new exercise prescription.
     * Handles both pre-formatted data (with modules/exercises at root) and
     * RoutineBuilder format (transforms into prescription structure).
     *
     * Xano: POST /exercise_prescription
     * Xano staging file: 666_exercise_prescription_POST.xs
     * Xano production file: 416_exercise_prescription_POST.xs
     *
     * @param {Object} data - Prescription data (RoutineBuilder or direct format)
     * @returns {Promise<import('@prism-ehr/shared').ExercisePrescription>}
     */
    async createExercisePrescription(data) {
      // If data is already in the correct format (has modules or exercises at root level),
      // pass it through as is
      if (data.modules !== undefined || data.exercises !== undefined) {
        return this.request('/exercise_prescription', {
          method: 'POST',
          body: JSON.stringify(data)
        });
      }

      // Otherwise, transform the data from the RoutineBuilder format
      const prescriptionData = {
        patient_id: data.patient_id,
        provider_id: data.provider_id,
        organization_id: data.organization_id,
        name: data.name,
        description: data.description,
        prescription_type: data.prescription_type,
        start_date: new Date().toISOString().split('T')[0],
        status: data.status || 'active',
        is_template: data.is_template || false,
        template_visibility: data.template_visibility || 'private',

        // Schedule mapping
        schedule: {
          rehab_frequency: data.prescription_type === 'rehabilitation' ? {
            times_per_week: data.schedule.frequency === '3x_week' ? 3 :
              data.schedule.frequency === '5x_week' ? 5 : 7,
            times_per_day: 1
          } : undefined,
          workout_schedule: data.prescription_type === 'fitness' ? {
            monday: data.schedule.frequency !== 'daily' ? 'rest' : 'workout',
            tuesday: 'workout',
            wednesday: data.schedule.frequency === '3x_week' ? 'rest' : 'workout',
            thursday: 'workout',
            friday: data.schedule.frequency === '3x_week' ? 'rest' : 'workout',
            saturday: data.schedule.frequency === '5x_week' ? 'rest' : 'workout',
            sunday: 'rest'
          } : undefined
        },

        // For rehabilitation - flat exercise list
        exercises: data.prescription_type === 'rehabilitation' && data.exercises ?
          data.exercises.map((ex, index) => ({
            exercise_id: ex.id,
            sequence: index + 1,
            parameters: {
              sets: ex.parameters?.sets || '',
              reps: ex.parameters?.reps || '',
              hold_time: ex.parameters?.hold_time || '',
              frequency: ex.parameters?.frequency || '',
              weight: ex.parameters?.weight || '',
              instructions: ex.parameters?.instructions || '',
              precautions: ex.precautions || []
            },
            is_active: true
          })) : [],

        // For fitness - modular structure
        modules: data.prescription_type === 'fitness' && data.modules ?
          data.modules.map((module, moduleIndex) => ({
            module_id: module.id,
            module_type: module.id,
            module_name: module.name,
            sequence: moduleIndex + 1,
            exercises: module.exercises.map((ex, exIndex) => ({
              exercise_id: ex.id,
              sequence: exIndex + 1,
              group_id: data.exercise_groups && Object.values(data.exercise_groups)
                .find(group => group.exerciseIds.includes(ex.uniqueId))?.id,
              parameters: {
                sets: ex.parameters?.sets || '',
                reps: ex.parameters?.reps || '',
                rest_time: ex.parameters?.rest_time || '',
                weight: ex.parameters?.weight || '',
                instructions: ex.parameters?.instructions || '',
                track_weight: true,
                track_pain: false
              }
            }))
          })) : [],

        // Exercise groups (for fitness)
        exercise_groups: data.exercise_groups ? Object.values(data.exercise_groups).map(group => ({
          group_id: group.id,
          group_type: group.type,
          name: group.name,
          instructions: group.instructions || '',
          rest_between_rounds: group.restBetweenRounds,
          rounds: group.rounds
        })) : []
      };

      return this.request('/exercise_prescription', {
        method: 'POST',
        body: JSON.stringify(prescriptionData)
      });
    },

    /**
     * Update an existing exercise prescription.
     *
     * Xano: PATCH /exercise_prescription/{id}
     * Xano staging file: 667_exercise_prescription_exercise_prescription_id_PATCH.xs
     * Xano production file: 419_exercise_prescription_exercise_prescription_id_PATCH.xs
     *
     * @param {number|string} id - Prescription ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').ExercisePrescription>}
     */
    async updateExercisePrescription(id, data) {
      return this.request(`/exercise_prescription/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get exercise prescriptions with optional filters.
     *
     * Xano: GET /exercise_prescription
     * Xano staging file: 665_exercise_prescription_GET.xs
     * Xano production file: 415_exercise_prescription_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.provider_id]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.prescription_type] - 'rehabilitation' or 'fitness'
     * @param {string} [filters.status]
     * @returns {Promise<import('@prism-ehr/shared').ExercisePrescription[]>}
     */
    async getExercisePrescriptions(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.prescription_type) queryParams.append('prescription_type', filters.prescription_type);
      if (filters.status) queryParams.append('status', filters.status);

      return this.request(`/exercise_prescription?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Duplicate an existing exercise prescription.
     *
     * Xano: POST /exercise_prescription/{id}/duplicate
     * Xano staging file: 877_exercise_prescription_exercise_prescription_id_duplicate_POST.xs
     * Xano production file: 420_exercise_prescription_exercise_prescription_id_duplicate_POST.xs
     *
     * @param {number|string} prescriptionId - Prescription ID to duplicate
     * @returns {Promise<import('@prism-ehr/shared').ExercisePrescription>}
     */
    async duplicateExercisePrescription(prescriptionId) {
      return this.request(`/exercise_prescription/${prescriptionId}/duplicate`, {
        method: 'POST'
      });
    },

    /**
     * Get a single exercise prescription by ID.
     *
     * Xano: GET /exercise_prescription/{id}
     * Xano staging file: 664_exercise_prescription_exercise_prescription_id_GET.xs
     * Xano production file: 505_exercise_prescription_exercise_prescription_id_GET.xs
     *
     * @param {number|string} id - Prescription ID
     * @returns {Promise<import('@prism-ehr/shared').ExercisePrescription>}
     */
    async getExercisePrescription(id) {
      return this.request(`/exercise_prescription/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single exercise prescription by ID (public/unauthenticated).
     *
     * Xano: GET /public/exercise_prescription/{id}
     * Xano staging file: 918_public_exercise_prescription_exercise_prescription_id_GET.xs
     * Xano production file: 511_public_exercise_prescription_exercise_prescription_id_GET.xs
     *
     * @param {number|string} id - Prescription ID
     * @returns {Promise<import('@prism-ehr/shared').ExercisePrescription>}
     */
    async getExercisePrescriptionPublic(id) {
      return this.publicRequest(`/public/exercise_prescription/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Delete an exercise prescription.
     *
     * Xano: DELETE /exercise_prescription/{id}
     * Xano staging file: 663_exercise_prescription_exercise_prescription_id_DELETE.xs
     * Xano production file: 417_exercise_prescription_exercise_prescription_id_DELETE.xs
     *
     * @param {number|string} id - Prescription ID
     * @returns {Promise<void>}
     */
    async deleteExercisePrescription(id) {
      return this.request(`/exercise_prescription/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ AI Routine Generation (SSE Streaming) ============

    /**
     * Generate an AI-powered exercise routine via server-sent events.
     * Uses direct fetch (not this.request) for SSE streaming support.
     *
     * Xano: POST /exercises/generate-routine
     * Xano staging file: 682_exercises_generate_routine_POST.xs
     * Xano production file: 422_exercises_generate_routine_POST.xs
     *
     * @param {Object} params - Generation parameters (mode, exercise preferences, etc.)
     * @param {Function} onChunk - Called for each parsed chunk (module, group, groupEnd, exercise)
     * @param {Function} onComplete - Called when stream completes with parsed exercises array
     * @param {Function} onError - Called on error
     * @returns {Promise<void>}
     */
    async generateRoutineStream(params, onChunk, onComplete, onError) {
      try {
        const token = authService.getToken();
        if (!token) {
          throw new Error('No authentication token found');
        }

        const requestBody = {
          mode: params.mode,
          parameters: params
        };

        // Since EventSource only supports GET, we use fetch with ReadableStream
        const response = await fetch(`${this.baseUrl}/exercises/generate-routine`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream'
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let contentBuffer = '';
        let parsedExercises = [];
        let currentModule = null;

        const processContentLine = (line) => {

          // Check if it's a MODULE line
          if (line.startsWith('MODULE|')) {
            const parts = line.split('|');
            currentModule = {
              type: 'module',
              name: parts[1],
              duration: parseInt(parts[2]) || 0
            };
            onChunk(currentModule);
          }
          // Check if it's a GROUP start line
          else if (line.startsWith('GROUP|')) {
            const parts = line.split('|');
            const groupChunk = {
              type: 'group',
              groupType: parts[1], // 'superset' or 'circuit'
              groupName: parts[2]
            };
            onChunk(groupChunk);
          }
          // Check if it's a GROUP end line
          else if (line.startsWith('GROUPEND')) {
            const groupEndChunk = {
              type: 'groupEnd'
            };
            onChunk(groupEndChunk);
          }
          // Check if it's an EXERCISE line
          else if (line.startsWith('EXERCISE|')) {
            const parts = line.split('|');
            const exercise = {
              type: 'exercise',
              id: parts[1],
              sets: parts[2],
              reps: parts[3],
              rest: parts[4],
              notes: parts[5] || '',
              module: currentModule?.name || 'main'
            };
            parsedExercises.push(exercise);
            onChunk(exercise);
          }
        };

        // Read the stream
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          // Decode the chunk and add to buffer
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete SSE messages
          const messages = buffer.split('\n\n');
          buffer = messages.pop() || ''; // Keep incomplete message in buffer

          for (const message of messages) {
            const lines = message.split('\n');

            for (const line of lines) {
              // Handle SSE data lines (matching Xano's format)
              if (line.startsWith('data: ')) {
                const data = line.substring(6);

                if (data === '[DONE]') {
                  // Process any remaining content
                  if (contentBuffer.trim()) {
                    const contentLines = contentBuffer.split('\n');
                    contentLines.forEach(contentLine => {
                      if (contentLine.trim()) {
                        processContentLine(contentLine.trim());
                      }
                    });
                  }
                  onComplete(parsedExercises);
                  return;
                }

                try {
                  const jsonData = JSON.parse(data);

                  // Extract content (matching Xano's example structure)
                  if (
                    jsonData.choices &&
                    jsonData.choices[0] &&
                    jsonData.choices[0].delta &&
                    jsonData.choices[0].delta.content
                  ) {
                    contentBuffer += jsonData.choices[0].delta.content;

                    // Check if we have complete lines to process
                    if (contentBuffer.includes('\n')) {
                      const contentLines = contentBuffer.split('\n');
                      contentBuffer = contentLines.pop() || ''; // Keep incomplete line

                      contentLines.forEach(contentLine => {
                        if (contentLine.trim()) {
                          processContentLine(contentLine.trim());
                        }
                      });
                    }
                  }
                } catch (error) {
                  console.error('Failed to parse JSON:', error, 'Data:', data);
                }
              }
            }
          }
        }

        // If we get here without [DONE], process remaining content
        if (contentBuffer.trim()) {
          const contentLines = contentBuffer.split('\n');
          contentLines.forEach(contentLine => {
            if (contentLine.trim()) {
              processContentLine(contentLine.trim());
            }
          });
        }
        onComplete(parsedExercises);

      } catch (error) {
        console.error('Stream generation error:', error);
        onError(error);
      }
    },

    // ============ Batch Exercise Retrieval ============

    /**
     * Get multiple exercises by their IDs in a single request.
     *
     * Xano: GET /exercises/get-batch
     * Xano staging file: 683_exercises_get_batch_GET.xs
     * Xano production file: 423_exercises_get_batch_GET.xs
     *
     * @param {Array<number|string>} exerciseIds - Array of exercise IDs
     * @returns {Promise<import('@prism-ehr/shared').Exercise[]>}
     */
    async getExercisesByIds(exerciseIds) {
      if (!exerciseIds || exerciseIds.length === 0) {
        return [];
      }

      let ids = exerciseIds.join(",");

      try {
        // Call the batch endpoint
        const response = await this.request(`/exercises/get-batch?exercise_ids=${ids}`, {
          method: 'GET'
        });

        // Handle empty or invalid responses
        if (!response || !Array.isArray(response)) {
          console.error('Invalid response from get-batch:', response);
          return [];
        }

        return response;
      } catch (error) {
        console.error('Failed to fetch exercises by IDs:', error);
        return [];
      }
    },

    // ============ Exercise Media Upload ============

    /**
     * Upload exercise media (image/GIF + optional video) via file upload.
     *
     * Xano: POST /exercise_media/upload
     * Xano staging file: 908_exercise_media_upload_POST.xs
     * Xano production file: 526_exercise_media_upload_POST.xs
     *
     * @param {File} imageFile - Image or GIF file
     * @param {Object} exerciseData
     * @param {string} exerciseData.name - Exercise name
     * @param {string} exerciseData.exercise_type - Exercise type
     * @param {boolean} exerciseData.copyrightConfirmed - Copyright confirmation
     * @param {File} [videoFile=null] - Optional video file
     * @param {Function} [onProgress=null] - Optional progress callback
     * @param {string} [exerciseId=null] - Existing exercise ID for edit mode
     * @returns {Promise<import('@prism-ehr/shared').ExerciseMediaUploadResult>}
     */
    async uploadExerciseMedia(imageFile, exerciseData, videoFile = null, onProgress = null, exerciseId = null) {
      const formData = new FormData();

      // Add the required image/GIF file
      formData.append('file', imageFile, imageFile.name.toLowerCase());

      // Add optional video file if provided
      if (videoFile) {
        formData.append('video_file', videoFile, videoFile.name.toLowerCase());
      }

      // Add exercise metadata
      formData.append('exercise_name', exerciseData.name);
      formData.append('exercise_type', exerciseData.exercise_type);
      formData.append('copyright_confirmed', exerciseData.copyrightConfirmed);

      // Explicitly send the MIME types as separate fields
      formData.append('mime_type', imageFile.type);
      if (videoFile) {
        formData.append('video_mime_type', videoFile.type);
      }

      if (exerciseId) {
        formData.append('exercise_id', exerciseId);
      }

      return this.request('/exercise_media/upload', {
        method: 'POST',
        timeoutMs: 120000,
        body: formData
      });
    },

    /**
     * Upload exercise media from URL sources (image URL + optional video URL).
     *
     * Xano: POST /exercise_media/upload
     * Xano staging file: 908_exercise_media_upload_POST.xs
     * Xano production file: 526_exercise_media_upload_POST.xs
     *
     * @param {string} imageUrl - URL of the image source
     * @param {Object} exerciseData
     * @param {string} exerciseData.name - Exercise name
     * @param {string} exerciseData.exercise_type - Exercise type
     * @param {boolean} exerciseData.copyrightConfirmed - Copyright confirmation
     * @param {string} [videoUrl=null] - Optional video URL
     * @param {string} [exerciseId=null] - Existing exercise ID for edit mode
     * @returns {Promise<import('@prism-ehr/shared').ExerciseMediaUploadResult>}
     */
    async uploadExerciseFromUrl(imageUrl, exerciseData, videoUrl = null, exerciseId = null) {
      // Build the request payload
      const payload = {
        url_source: imageUrl,
        exercise_name: exerciseData.name,
        exercise_type: exerciseData.exercise_type,
        media_type: 'image',
        copyright_confirmed: exerciseData.copyrightConfirmed
      };

      // Add video URL if provided
      if (videoUrl) {
        payload.video_url_source = videoUrl;
      }

      if (exerciseId) {
        payload.exercise_id = exerciseId;
      }

      return this.request('/exercise_media/upload', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    },

  };
}
